import { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners
} from '@dnd-kit/core';
import { Box, Card, CardContent, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { KanbanColumn } from './KanbanColumn.js';
import type { Lead, PipelineEtapa } from '@azoupvendas/shared';

const ETAPAS: PipelineEtapa[] = ['novo', 'qualificado', 'proposta', 'ganho', 'perdido'];

interface KanbanBoardProps {
  byEtapa: Record<PipelineEtapa, Lead[]>;
  onCardClick: (lead: Lead) => void;
  onEtapaChange: (leadId: string, novaEtapa: PipelineEtapa) => void;
}

// AIDEV-NOTE: Ghost card para DragOverlay — não usa useSortable pois está fora de SortableContext
function LeadCardGhost({ lead }: { lead: Lead }) {
  return (
    <Card
      sx={{
        mb: 1,
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        boxShadow: 6,
        opacity: 0.95,
        userSelect: 'none'
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" alignItems="flex-start" gap={0.5}>
          <Box sx={{ color: 'text.disabled', mt: '2px', flexShrink: 0 }}>
            <DragIndicatorIcon fontSize="small" />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {lead.nome}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// AIDEV-NOTE: DnD via @dnd-kit — PointerSensor com activationConstraint para evitar clique acidental
// AIDEV-NOTE: DragOverlay usa LeadCardGhost (sem useSortable) para evitar erro fora de SortableContext
export function KanbanBoard({ byEtapa, onCardClick, onEtapaChange }: KanbanBoardProps) {
  const [activeCard, setActiveCard] = useState<Lead | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  function handleDragStart(event: DragStartEvent) {
    const allLeads = ETAPAS.flatMap((e) => byEtapa[e] ?? []);
    const lead = allLeads.find((l) => l.id === event.active.id);
    if (lead) setActiveCard(lead);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveCard(null);
    const { active, over } = event;
    if (!over) return;

    const leadId = active.id as string;
    const overId = over.id as string;

    // AIDEV-NOTE: over.id pode ser uma etapa (drop na coluna vazia) ou um lead ID (drop sobre card).
    // Se for lead ID, derivamos a etapa destino procurando em qual coluna esse lead está.
    let novaEtapa: PipelineEtapa;
    if (ETAPAS.includes(overId as PipelineEtapa)) {
      novaEtapa = overId as PipelineEtapa;
    } else {
      const etapaDoCard = ETAPAS.find((e) => (byEtapa[e] ?? []).some((l) => l.id === overId));
      if (!etapaDoCard) return;
      novaEtapa = etapaDoCard;
    }

    const etapaAtual = ETAPAS.find((e) => (byEtapa[e] ?? []).some((l) => l.id === leadId));
    if (etapaAtual !== novaEtapa) {
      onEtapaChange(leadId, novaEtapa);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box display="flex" gap={2} overflow="auto" pb={2}>
        {ETAPAS.map((etapa) => (
          <KanbanColumn
            key={etapa}
            etapa={etapa}
            leads={byEtapa[etapa] ?? []}
            onCardClick={onCardClick}
          />
        ))}
      </Box>

      <DragOverlay>{activeCard && <LeadCardGhost lead={activeCard} />}</DragOverlay>
    </DndContext>
  );
}
